import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'

import { ABIParameter } from '@remixproject/plugin-api'
import { decodeUint, encodeKey, encodeUint, encrypt, itString, itUint, sign } from '@coti-io/coti-sdk-typescript'
import { BrowserProvider, id, JsonRpcSigner, solidityPacked, solidityPackedKeccak256 } from 'ethers'

import Button from 'react-bootstrap/Button'
import CloseButton from 'react-bootstrap/CloseButton'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'

import { ENCRYPTED_INPUT_TYPES } from '../../utils/constants'
import { useEnvironmentStore } from '../../zustand/environmentStore'
import { JsonRpcProvider, Wallet } from 'ethers'
import { useWeb3ModalProvider } from '@web3modal/ethers/react'

type TransactionInputProps = {
    contractAddress?: string,
    functionName?: string,
    functionInputs?: ABIParameter[],
    index: number,
    type: string,
    internalType: string,
    name: string
}

export interface TransactionInputHandle {
    handleUpdateValue: () => Promise<any>;
  }

type InputValue = {
    name: string,
    type: "primitive" | "array" | "tuple",
    baseType?: string,
    plaintext?: string
    ciphertext?: bigint | { value: bigint[] },
    signature?: Uint8Array | string | (Uint8Array | string)[],
    components?: { [index: number]: InputValue }
}

const TransactionInput = forwardRef<TransactionInputHandle, TransactionInputProps>(({ contractAddress, functionName, functionInputs, index, type, internalType, name }, ref) => {
    const [inputType, setInputType] = useState('')
    const [encryptedType, setEncryptedType] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const { environment, privateKey, rpcUrl, userKey } = useEnvironmentStore()
    const { walletProvider } = useWeb3ModalProvider()
    const [value, setValue] = useState('')
    const [plaintext, setPlaintext] = useState<any>('')
    const [ciphertext, setCiphertext] = useState<any>('')

    useEffect(() => {
        const internalTypeArr = internalType.split(' ')

        if (internalTypeArr.length === 2 && ENCRYPTED_INPUT_TYPES.includes(internalTypeArr[1].replace(/[\[\]']+/g, ''))) {
            setInputType(internalTypeArr[1])
        } else {
            setInputType(type)
        }
    }, [])

    const computeFunctionSignature = (functionName: string, functionInputs: ABIParameter[]) => {
        const computeFunctionSignatureHelper = (components: ABIParameter[]): string => {
            const inputs: string[] = []
            
            components.map((component, index) => {
                if ('components' in component) {
                    let componentInputs = computeFunctionSignatureHelper(component.components!)

                    let componentType = component.type

                    while (componentType.slice(-2) === '[]') {
                        componentInputs += '[]'
                        componentType = componentType.slice(0, componentType.length - 2)
                    }

                    inputs.push(componentInputs)
                } else {
                    inputs.push(component.type)
                }
            })

            const inputStr = '(' + inputs.toString() + ')'

            return inputStr
        }

        const inputs = computeFunctionSignatureHelper(functionInputs)

        return id(`${functionName}${inputs}`).slice(0, 10)
    }

    const buildInputText = async (
        plaintext: bigint,
        signer: JsonRpcSigner | Wallet,
        contractAddress: string,
        functionSelector: string
    ) => {
        if (plaintext >= BigInt(2) ** BigInt(64)) {
            throw new RangeError("Plaintext size must be 64 bits or smaller.")
        }
    
        // Convert the plaintext to bytes
        const plaintextBytes = encodeUint(plaintext)
    
        // Convert user key to bytes
        const keyBytes = encodeKey(userKey)
    
        // Encrypt the plaintext using AES key
        const {ciphertext, r} = encrypt(keyBytes, plaintextBytes)
        const ct = new Uint8Array([...ciphertext, ...r])
    
        // Convert the ciphertext to BigInt
        const ctInt = decodeUint(ct)
    
        
        let signature: Uint8Array | string
        
        if (signer instanceof JsonRpcSigner) {
            const message = solidityPacked(
                ["address", "address", "bytes4", "uint256"],
                [signer.address, contractAddress, functionSelector, ctInt]
            )

            const messageBytes = new Uint8Array((message.length - 2) / 2)

            for (let i = 0; i < message.length - 2; i += 2) {
                const byte = parseInt(message.substring(i + 2, i + 4), 16)
                messageBytes[i / 2] = byte
            }

            signature = await signer.signMessage(messageBytes)
        } else {
            const message = solidityPackedKeccak256(
                ["address", "address", "bytes4", "uint256"],
                [signer.address, contractAddress, functionSelector, ctInt]
            )

            signature = sign(message, signer.privateKey);
        }
    
        return {
            ciphertext: ctInt,
            signature: signature
        }
    }

    async function buildStringInputText(
        plaintext: string,
        signer: JsonRpcSigner | Wallet,
        contractAddress: string,
        functionSelector: string
    ) {
        let encoder = new TextEncoder()
    
        let encodedStr = encoder.encode(plaintext)
    
        const inputText = {
            ciphertext: { value: new Array<bigint> },
            signature: new Array<Uint8Array | string>
        }
    
        for (let i = 0; i < encodedStr.length / 8; i++) {
            const startIdx = i * 8
            const endIdx = Math.min(startIdx + 8, encodedStr.length)
    
            const byteArr = new Uint8Array([...encodedStr.slice(startIdx, endIdx), ...new Uint8Array(8 - (endIdx - startIdx))])
    
            const it = await buildInputText(
                decodeUint(byteArr),
                signer,
                contractAddress,
                functionSelector
            )
    
            inputText.ciphertext.value.push(it.ciphertext)
            inputText.signature.push(it.signature)
        }
    
        return inputText
    }

    const formatInputData = async (data: any, abi: ABIParameter) => {
        const formatInputDataHelper = async (signer: JsonRpcSigner | Wallet, value: any, input: ABIParameter) => {
            const formatArray = async (signer: JsonRpcSigner | Wallet, arrayValue: any[], arrayInput: ABIParameter) => {
                let data: InputValue = {
                    name: arrayInput.name,
                    type: 'array',
                    baseType: arrayInput.type.slice(0, arrayInput.type.length - 2),
                    components: {}
                }

                if (arrayInput.components) {
                    if (arrayInput.type.slice(0, arrayInput.type.length - 2).includes('[]')) {
                        const componentOutput = { ...arrayInput }

                        componentOutput.type = componentOutput.type.slice(0, componentOutput.type.length - 2)

                        if ('internalType' in componentOutput) {
                            componentOutput.internalType = (componentOutput.internalType as string).slice(0, (componentOutput.internalType as string).length - 2)
                        }

                        for (const [idx, nestedArr] of arrayValue.entries()) {
                            data.components![idx] = await formatInputDataHelper(signer, nestedArr, componentOutput)
                        }
                    } else {
                        let baseType = arrayInput.type

                        if ('internalType' in arrayInput) {
                            const internalType = arrayInput.internalType as string
                            baseType = internalType.split(' ')[1]
                            baseType = baseType.slice(0, baseType.length - 2)
                        }
                        
                        if (ENCRYPTED_INPUT_TYPES.includes(baseType)) {
                            data.baseType = baseType

                            for (const [idx, v] of arrayValue.entries()) {
                                const inputText = await buildInputText(
                                    BigInt(v),
                                    signer,
                                    contractAddress!,
                                    computeFunctionSignature(functionName!, functionInputs!)
                                )
            
                                data.components![idx] = {
                                    name: "",
                                    type: "tuple",
                                    baseType: baseType,
                                    plaintext: v,
                                    ciphertext: inputText.ciphertext,
                                    signature: inputText.signature
                                }
                            }
                        } else {
                            for (const [idx1, v] of arrayValue.entries()) {
                                const tuple: InputValue = {
                                    name: arrayInput.name,
                                    type: "tuple",
                                    components: {}
                                }

                                for (const [idx2, component] of arrayInput.components!.entries()) {
                                    tuple.components![idx2] = await formatInputDataHelper(signer, v[idx2], component)
                                }
        
                                data.components![idx1] = tuple
                            }
                        }
                    }
                } else {
                    if (arrayInput.type.slice(0, arrayInput.type.length - 2).includes('[]')) {
                        const componentOutput: ABIParameter = {
                            name: arrayInput.name,
                            type: arrayInput.type.slice(0, arrayInput.type.length - 2)
                        }

                        for (const [idx, nestedArr] of arrayValue.entries()) {
                            data.components![idx] = await formatInputDataHelper(signer, nestedArr, componentOutput)
                        }
                    } else {
                        arrayValue.forEach((v: any, index: number) => {
                            data.components![index] = {
                                name: "",
                                type: "primitive",
                                baseType: arrayInput.type.slice(0, arrayInput.type.length - 2),
                                plaintext: v
                            }
                        })
                    }
                }

                return data
            }

            const formatTuple = async (tupleValue: any, tupleInput: ABIParameter) => {
                let data: InputValue = {
                    name: tupleInput.name,
                    type: 'tuple',
                    components: {}
                }

                let baseType = tupleInput.type

                if ('internalType' in tupleInput) {
                    baseType = (tupleInput.internalType as string).split(' ')[1]
                }
                
                if (ENCRYPTED_INPUT_TYPES.includes(baseType)) {
                    let inputText: itUint | itString

                    if (baseType === 'itString') {
                        inputText = await buildStringInputText(
                            tupleValue,
                            signer,
                            contractAddress!,
                            computeFunctionSignature(functionName!, functionInputs!)
                        )
                    } else {
                        inputText = await buildInputText(
                            BigInt(tupleValue),
                            signer,
                            contractAddress!,
                            computeFunctionSignature(functionName!, functionInputs!)
                        )
                    }
                    
                    data = {
                        name: tupleInput.name,
                        type: "tuple",
                        baseType: baseType,
                        plaintext: tupleValue,
                        ciphertext: inputText.ciphertext,
                        signature: inputText.signature
                    }
                } else {
                    for (const [index, component] of tupleInput.components!.entries()) {
                        data.components![index] = await formatInputDataHelper(signer, tupleValue[index], component)
                    }
                }

                return data
            }

            const formatPrimitive = (primitiveValue: any, primitiveInput: ABIParameter): InputValue => {
                const baseType = 'internalType' in primitiveInput ? primitiveInput.internalType as string : primitiveInput.type

                return {
                    name: primitiveInput.name,
                    type: "primitive",
                    baseType: baseType,
                    plaintext: primitiveValue
                }
            }

            if (input.type.includes('[]')) {
                return await formatArray(signer, value, input)
            }
            
            if (input.components) {
                return await formatTuple(value, input)
            }
    
            return formatPrimitive(value, input)
        }

        let signer: JsonRpcSigner | Wallet

        if (environment === 'Wallet') {
            if (walletProvider === undefined) throw Error('Wallet provider is undefined')
    
            const ethersProvider = new BrowserProvider(walletProvider)

            signer = await ethersProvider.getSigner()
        } else if (environment === 'Manual') {
            const provider = new JsonRpcProvider(rpcUrl)

            signer = new Wallet(privateKey, provider)
        } else {
            throw new Error('Unrecognized environment type')
        }

        return await formatInputDataHelper(signer, data, abi)
    }

    const formatEncryptedInput = (inputValue: InputValue) => {
        const formatEncryptedInputHelper = (iv: InputValue) => {
            if (iv.components) {
                let componentIsEncryptedType = false
                const componentEncryptedValues: any[] = []
                const componentPlaintexts: any[] = []
                const componentCiphertexts: any[] = []

                Object.values(iv.components).forEach((component, index) => {
                    const [isEncryptedType, encryptedValue, plaintext, ciphertext] = formatEncryptedInputHelper(component)
                    
                    if (isEncryptedType) {
                        componentIsEncryptedType = true
                    }

                    componentEncryptedValues.push(encryptedValue)
                    componentPlaintexts.push(plaintext)
                    componentCiphertexts.push(ciphertext)
                })

                return [componentIsEncryptedType, componentEncryptedValues, `[${componentPlaintexts.toString()}]`, `[${componentCiphertexts.toString()}]`]
            } else {
                let isEncryptedType = false
                let encryptedValue: any = iv.plaintext
                let c: any = iv.plaintext

                if (iv.ciphertext) {
                    isEncryptedType = true
                    setEncryptedType(true)

                    if (environment === 'Wallet') {
                        if (iv.baseType === 'itString') {
                            const stringCiphertext = iv.ciphertext as { value: bigint[] }
                            c = [stringCiphertext.value.map((item) => item.toString())]

                            encryptedValue = [c, iv.signature]
                        } else {
                            c = iv.ciphertext.toString()
                            encryptedValue = [c, iv.signature]
                        }
                    } else if (environment === 'Manual') {
                        if (iv.baseType === 'itString') {
                            const stringCiphertext = iv.ciphertext as { value: bigint[] }
                            c = [stringCiphertext.value.map((item) => item.toString())]

                            const signatures = []

                            for (let i = 0; i < iv.signature!.length; i++) {
                                const formattedSig = []
        
                                const sig = iv.signature![i] as Uint8Array

                                for (let j = 0; j < sig.length; j++) {
                                    formattedSig.push(sig[j].toString(16).padStart(2, '0'))
                                }

                                signatures.push('0x' + formattedSig.join(""))
                            }
                            
                            encryptedValue = [c, signatures]
                        } else {
                            c = iv.ciphertext.toString()

                            let sig = []
    
                            for (let i = 0; i < iv.signature!.length; i++) {
                                sig.push(iv.signature![i].toString(16).padStart(2, '0'))
                            }
                            
                            encryptedValue = [c, '0x' + sig.join("")]
                        }
                    } else {
                        throw new Error('Unrecognized environment type')
                    }
                }

                return [isEncryptedType, encryptedValue, iv.plaintext, c]
            }
        }

        return formatEncryptedInputHelper(inputValue)
    }

    const handleUpdateValue = async () => {
        let v: string | any = value

        if (type.includes('[]') || type.includes('tuple')) {
            v = JSON.parse(value)
        }

        const inputData = await formatInputData(v, functionInputs![index])

        const [isEncryptedType, encryptedInput, p, c] = await formatEncryptedInput(inputData)
        
        if (!isEncryptedType) {
            return v
        }
        
        setPlaintext(p)
        setCiphertext(c)

        return encryptedInput
    }

    useImperativeHandle(ref, () => ({
        handleUpdateValue,
    }))
    
    return (
        <>
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header>
                    <CloseButton onClick={() => setShowModal(false)} />
                </Modal.Header>

                <Modal.Body className='encrypted-input-modal'>
                    <p>Decrypted value: {plaintext}</p>
                    <p>Encrypted value: {ciphertext.toString()}</p>
                </Modal.Body>
            </Modal>

            <Form.Group key={index}>
                <Form.Label>{inputType} {name}</Form.Label>
                <div className='transaction-input-form-control-container'>
                    <Form.Control type='text' size='sm' name={name} onChange={(e) => setValue(e.target.value)} className={encryptedType ? 'encrypted-input-form-control' : ''} required />

                    {encryptedType &&
                        <Button size='sm' variant='outline-primary' className='encrypted-input-modal-btn' onClick={() => setShowModal(true)}><b>i</b></Button>
                    }
                </div>
            </Form.Group>
        </>
    )
})

export default TransactionInput